import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Hook for resizable table columns.
 * @param {Object} initialWidths - e.g. { data: 100, nome: 200, rsa: 160 }
 * @param {number} minWidth - minimum column width in px (default 60)
 * @returns {{ widths, onMouseDown, headerStyle, cellStyle }}
 */
export default function useResizableColumns(initialWidths, minWidth = 60) {
  const [widths, setWidths] = useState(initialWidths);
  const dragging = useRef(null);

  const onMouseDown = useCallback(
    (colKey, e) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = widths[colKey] || 100;

      const onMouseMove = (moveEvent) => {
        const delta = moveEvent.clientX - startX;
        const newWidth = Math.max(minWidth, startWidth + delta);
        setWidths((prev) => ({ ...prev, [colKey]: newWidth }));
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        dragging.current = null;
      };

      dragging.current = colKey;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [widths, minWidth],
  );

  const headerStyle = useCallback(
    (colKey) => ({
      width: widths[colKey],
      minWidth: minWidth,
      position: "relative",
    }),
    [widths, minWidth],
  );

  const cellStyle = useCallback(
    (colKey) => ({
      width: widths[colKey],
      minWidth: minWidth,
      maxWidth: widths[colKey],
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
    [widths, minWidth],
  );

  return { widths, onMouseDown, headerStyle, cellStyle };
}
